import { PermissionCode, halloSalutation, type AuthUser } from '@gb-ticket/shared-types';
import type { DragEndEvent } from '@dnd-kit/core';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { useVoice } from '@/platform/voice/use-voice';
import {
  DashboardCustomizeToolbar,
  DashboardEmptyLayout,
  DashboardHiddenPalette,
} from '@/products/helpdesk/features/dashboard/dashboard-customize';
import {
  asDashboardDragData,
  DashboardDndProvider,
  DashboardZoneSortableContext,
} from '@/products/helpdesk/features/dashboard/dashboard-dnd';
import {
  DASHBOARD_ZONE_IDS,
  hideDashboardWidget,
  hideDashboardZone,
  moveDashboardWidget,
  moveDashboardZone,
  reorderDashboardWidgets,
  reorderDashboardZones,
  showDashboardWidget,
  showDashboardZone,
  visibleDashboardZoneOrder,
  type DashboardWidgetId,
  type DashboardZoneId,
} from '@/products/helpdesk/features/dashboard/dashboard-layout';
import { DashboardZone } from '@/products/helpdesk/features/dashboard/dashboard-zone';
import { useDashboardLayout } from '@/products/helpdesk/features/dashboard/use-dashboard-layout';
import { getDashboardWidgetDef } from '@/products/helpdesk/features/dashboard/dashboard-widget-registry';
import { hasPermission } from '@/features/auth/permissions';

export function AgentWorkDashboard({ user }: { user: AuthUser }) {
  const voice = useVoice();
  const canCreate = hasPermission(user, PermissionCode.TICKETS_CREATE);
  const { layout, setLayout, resetLayout } = useDashboardLayout(user.id);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    if (!editMode) {
      return;
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        setEditMode(false);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [editMode]);

  const visibleZones = visibleDashboardZoneOrder(layout);

  const hiddenZones = layout.zoneHidden.filter((id) =>
    (DASHBOARD_ZONE_IDS as readonly string[]).includes(id),
  );

  const hiddenWidgets = (DASHBOARD_ZONE_IDS as readonly DashboardZoneId[]).flatMap((zoneId) =>
    layout.zones[zoneId].hidden
      .filter((widgetId) => getDashboardWidgetDef(widgetId).isAvailable(user))
      .map((widgetId) => ({ zoneId, widgetId })),
  );

  function onHideZone(zoneId: DashboardZoneId) {
    setLayout((prev) => hideDashboardZone(prev, zoneId));
  }

  function onShowZone(zoneId: DashboardZoneId) {
    setLayout((prev) => showDashboardZone(prev, zoneId));
  }

  function onHideWidget(zoneId: DashboardZoneId, widgetId: DashboardWidgetId) {
    setLayout((prev) => hideDashboardWidget(prev, zoneId, widgetId));
  }

  function onShowWidget(zoneId: DashboardZoneId, widgetId: DashboardWidgetId) {
    setLayout((prev) => showDashboardWidget(prev, zoneId, widgetId));
  }

  function onMoveZone(zoneId: DashboardZoneId, direction: 'up' | 'down') {
    setLayout((prev) => moveDashboardZone(prev, zoneId, direction));
  }

  function onMoveWidget(
    zoneId: DashboardZoneId,
    widgetId: DashboardWidgetId,
    direction: 'up' | 'down',
  ) {
    setLayout((prev) => moveDashboardWidget(prev, zoneId, widgetId, direction));
  }

  function showAllZones() {
    setLayout((prev) => ({
      ...prev,
      zoneHidden: [],
    }));
  }

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const activeData = asDashboardDragData(active.data.current);
    const overData = asDashboardDragData(over.data.current);
    if (!activeData || !overData) {
      return;
    }

    if (activeData.type === 'zone' && overData.type === 'zone') {
      setLayout((prev) => reorderDashboardZones(prev, activeData.zoneId, overData.zoneId));
      return;
    }

    if (
      activeData.type === 'widget' &&
      overData.type === 'widget' &&
      activeData.zoneId === overData.zoneId
    ) {
      setLayout((prev) =>
        reorderDashboardWidgets(prev, activeData.zoneId, activeData.widgetId, overData.widgetId),
      );
    }
  }

  const zones = (
    <div className="min-w-0 space-y-8">
      {visibleZones.map((zoneId) => (
        <DashboardZone
          key={zoneId}
          zoneId={zoneId}
          user={user}
          layout={layout}
          editMode={editMode}
          onHideZone={onHideZone}
          onHideWidget={onHideWidget}
          onMoveZone={onMoveZone}
          onMoveWidget={onMoveWidget}
          onStartCustomize={() => setEditMode(true)}
        />
      ))}
    </div>
  );

  return (
    <section className="min-w-0 space-y-8">
      <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight">{halloSalutation(user, voice)}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {editMode
              ? 'Anpassen: Zonen und Widgets verschieben oder ausblenden. Esc beendet den Modus.'
              : 'Persönliche Arbeitsschlange und IT-Lagebild.'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DashboardCustomizeToolbar
            editMode={editMode}
            onStartEdit={() => setEditMode(true)}
            onFinishEdit={() => setEditMode(false)}
            onReset={() => {
              resetLayout();
            }}
          />
          {canCreate ? (
            <Button
              asChild
              className="bg-brand-red text-white hover:bg-brand-red/90 focus-visible:ring-brand-red/30"
            >
              <Link to="/helpdesk/tickets/neu">Neues Ticket</Link>
            </Button>
          ) : null}
        </div>
      </div>

      {visibleZones.length === 0 ? (
        <DashboardEmptyLayout onCustomize={() => setEditMode(true)} onShowAllZones={showAllZones} />
      ) : (
        <DashboardDndProvider enabled={editMode} onDragEnd={onDragEnd}>
          {editMode ? (
            <DashboardZoneSortableContext zoneIds={visibleZones}>
              {zones}
            </DashboardZoneSortableContext>
          ) : (
            zones
          )}
        </DashboardDndProvider>
      )}

      {editMode ? (
        <DashboardHiddenPalette
          hiddenZones={hiddenZones}
          hiddenWidgets={hiddenWidgets}
          onShowZone={onShowZone}
          onShowWidget={onShowWidget}
        />
      ) : null}
    </section>
  );
}
